import { useCallback, useEffect, useState } from "react";
import { IoPricetags } from "react-icons/io5";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroCategoriaEstoque from "../../components/modais/ModalCadastroCategoriaEstoque";
import { api } from "../../services/api";

const AJUDA = {
  titulo: "Cadastro de Subcategorias de Estoque",
  descricao:
    "Subcategorias detalham as Categorias de estoque (ex.: dentro de 'Insumos', as subcategorias 'Inseticidas', 'Fertilizantes'). Elas são usadas para classificar os Itens de Estoque com mais precisão nas listas e filtros.",
  secoes: [
    {
      titulo: "Dados da Subcategoria",
      campos: [
        {
          nome: "Nome da Subcategoria",
          obrigatorio: true,
          explicacao:
            "Nome curto que identifica o grupo mais específico de itens (ex.: 'Sementes', 'Ferramentas Manuais').",
        },
        {
          nome: "Categoria Principal",
          obrigatorio: true,
          explicacao:
            "A categoria (cadastrada em Categorias de Estoque) à qual essa subcategoria pertence. Se a categoria desejada ainda não existir, use o botão de atalho ao lado do campo para criá-la sem sair da tela.",
        },
      ],
    },
  ],
  dicas: [
    "Antes de criar uma subcategoria nova, verifique na lista se já não existe uma parecida na mesma categoria.",
  ],
};

export default function GestaoSubcategorias() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [subcategorias, setSubcategorias] = useState([]);
  const [subcategoriasFiltradas, setSubcategoriasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para o select de categorias
  const [categorias, setCategorias] = useState([]);
  const [mostrarModalCategoria, setMostrarModalCategoria] = useState(false);

  const [filtros, setFiltros] = useState({
    busca: "",
    categoria: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    categoria_principal_id: "",
  });

  // Carregar categorias principais
  const carregarCategorias = async () => {
    try {
      const response = await api.get("/almoxarifado/categorias/");
      const dados = response.data.results || response.data;
      setCategorias(dados);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  };

  // Carregar subcategorias
  const carregarSubcategorias = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await api.get("/almoxarifado/subcategorias/");
      const dados = response.data.results || response.data;
      setSubcategorias(dados);
      setSubcategoriasFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar subcategorias:", error);
      alert("Erro ao carregar lista de subcategorias");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarSubcategorias();
    carregarCategorias();
  }, [carregarSubcategorias]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Preencha o nome da subcategoria.");
      return false;
    }
    if (!formData.categoria_principal_id) {
      alert("Selecione uma categoria principal.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
      categoria_principal_id: formData.categoria_principal_id,
    };

    try {
      if (editando) {
        const response = await api.put(`/almoxarifado/subcategorias/${editandoId}/`, payload);
        if (response.status === 200) {
          alert("Subcategoria atualizada com sucesso!");
          handleCancel();
          carregarSubcategorias();
        }
      } else {
        const response = await api.post("/almoxarifado/subcategorias/", payload);
        if (response.status === 201 || response.status === 200) {
          alert("Subcategoria cadastrada com sucesso!");
          handleCancel();
          carregarSubcategorias();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (subcategoria) => {
    setFormData({
      nome: subcategoria.nome || "",
      categoria_principal_id: subcategoria.categoria_principal?.id || subcategoria.categoria_principal_id || "",
    });
    setEditando(true);
    setEditandoId(subcategoria.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaSubcategoria = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      categoria_principal_id: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta subcategoria? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await api.delete(`/almoxarifado/subcategorias/${id}/`);
      alert("Subcategoria excluída com sucesso!");
      carregarSubcategorias();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir subcategoria.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...subcategorias];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(s => 
        s.nome?.toLowerCase().includes(buscaLower) ||
        s.categoria_principal?.nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.categoria) {
      filtrados = filtrados.filter(s => 
        (s.categoria_principal?.id === parseInt(filtros.categoria)) ||
        (s.categoria_principal_id === parseInt(filtros.categoria))
      );
    }
    
    setSubcategoriasFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", categoria: "" });
    setSubcategoriasFiltradas(subcategorias);
  };

  // Obter nome da categoria principal
  const getCategoriaNome = (subcategoria) => {
    if (subcategoria.categoria_principal?.nome) {
      return subcategoria.categoria_principal.nome;
    }
    const categoria = categorias.find(c => c.id === (subcategoria.categoria_principal_id || subcategoria.categoria_principal));
    return categoria ? categoria.nome : "—";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Subcategorias <IoPricetags size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaSubcategoria}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Subcategoria
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Subcategoria" : "Cadastrar Nova Subcategoria"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Nome da Subcategoria */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Nome da Subcategoria *</label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Ex: Inseticidas, Fertilizantes, Sementes, etc."
                    required
                  />
                  <span className="text-xs text-gray-500 mt-1">
                    Nome descritivo para organizar os produtos
                  </span>
                </div>

                {/* Linha 2: Categoria Principal */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Categoria Principal *</label>
                  <div className="flex items-center gap-2">
                    <select
                      name="categoria_principal_id"
                      value={formData.categoria_principal_id}
                      onChange={handleChange}
                      className="w-96 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione uma categoria</option>
                      {categorias.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.nome}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setMostrarModalCategoria(true)}
                      title="Cadastrar nova categoria"
                      className="p-2 hover:bg-gray-200 rounded"
                    >
                      <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                    </button>
                  </div>
                  <span className="text-xs text-gray-500 mt-1">
                    A categoria à qual esta subcategoria pertence
                  </span>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome</label>
                  <input
                    type="text"
                    placeholder="Digite o nome da subcategoria..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Filtrar por Categoria</label>
                  <select
                    value={filtros.categoria}
                    onChange={(e) => setFiltros({...filtros, categoria: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todas as categorias</option>
                    {categorias.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Subcategorias */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {subcategoriasFiltradas.length} subcategoria(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Subcategoria</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Categoria Principal</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subcategoriasFiltradas.map((subcategoria) => (
                        <tr key={subcategoria.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{subcategoria.id}</td>
                          <td className="p-3 text-sm font-medium">
                            <span className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                              </svg>
                              {subcategoria.nome}
                            </span>
                          </td>
                          <td className="p-3 text-sm">
                            <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                              {getCategoriaNome(subcategoria)}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(subcategoria)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(subcategoria.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Excluir"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {subcategoriasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.categoria
                              ? "Nenhuma subcategoria encontrada com os filtros selecionados."
                              : "Nenhuma subcategoria cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroCategoriaEstoque
        isOpen={mostrarModalCategoria}
        onClose={() => setMostrarModalCategoria(false)}
        onSuccess={async (nova) => {
          await carregarCategorias();
          setFormData((prev) => ({ ...prev, categoria_principal_id: nova.id }));
        }}
      />
    </Layout>
  );
}